'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ShieldTick } from 'iconsax-react';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import { useDispatch } from 'react-redux';
import { openModal, useModal, closeModal } from '@/store/slices/Modals';
import CustomModal from '@/components/atoms/CustomModal';
import AddCertificateModal from './AddCertificateModal';
import PreviewProfileItem from '@/components/SellerProfileCreation/PreviewProfileItem';
import PreviewFile from '@/components/SellerProfileCreation/PreviewFile';
import Button from '@/components/atoms/Button';

interface CertificateItem {
  uid: string;
  name: string;
  url: string;
  certificateName: string;
  size?: number;
  type?: string;
}

const VerificationForm = () => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();
  const [editingCertificateId, setEditingCertificateId] = React.useState<
    string | null
  >(null);

  const dispatch = useDispatch();
  const { modals } = useModal();

  const acceptSampleOrder = watch('acceptSampleOrder');
  const productType = watch('productType') ?? 'cart';

  React.useEffect(() => {
    if (productType === 'cart') {
      setValue('acceptSampleOrder', false, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('minSampleQty', undefined, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [productType, setValue]);

  const certificatesDocuments = watch('certificatesDocuments') || [];
  const handleAddCertificateSuccess = (
    cert: CertificateItem,
    oldUid?: string
  ) => {
    if (oldUid) {
      setValue(
        'certificatesDocuments',
        certificatesDocuments.map((c: CertificateItem) =>
          c.uid === oldUid ? cert : c
        ),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    } else {
      setValue('certificatesDocuments', [...certificatesDocuments, cert], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const editingCertificate = certificatesDocuments.find(
    (c: CertificateItem) => c.uid === editingCertificateId
  );

  const handleDeleteCertificate = (uid: string) => {
    setValue(
      'certificatesDocuments',
      certificatesDocuments.filter((c: CertificateItem) => c.uid !== uid),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  return (
    <div
      className="bg-white dark:bg-dark-surface-primary p-4 md:p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border shadow-sm mb-8 animate-fade-in"
      data-testid="verification-card"
    >
      {/* Header */}
      <div
        className="mb-6 flex gap-4 items-center"
        data-testid="verification-header"
      >
        <div
          className="p-3 bg-soft-background dark:bg-dark-button-bg-secondary rounded-xl text-neutral-900 dark:text-dark-text-primary flex items-center justify-center"
          data-testid="verification-icon-container"
        >
          <ShieldTick variant="Bold" size={24} />
        </div>
        <div data-testid="verification-header-text">
          <TitleText
            text={t('verificationSectionTitle')}
            size="lg"
            className="!font-bold !text-[18px] text-[#090909] dark:text-dark-text-primary !leading-[100%] !tracking-[0px]"
          />
          <InfoText
            text={t('logisticsDataDesc')}
            size="xs"
            className="!font-normal !text-[12px] text-[#777777] dark:text-dark-text-secondary !leading-[100%] !tracking-[0px] mt-1"
          />
        </div>
      </div>

      <div className="flex flex-col gap-6" data-testid="verification-fields">
        {/* Accept Sample Orders */}
        {productType === 'rfq' && (
          <>
            <div
              className="flex items-center justify-between gap-4"
              data-testid="accept-sample-orders-row"
            >
              <div data-testid="accept-sample-orders-text">
                <TitleText
                  text={t('acceptSampleOrders')}
                  size="sm"
                  className="!font-semibold text-neutral-900 dark:text-dark-text-primary"
                />
                <InfoText
                  text={t('acceptSampleOrdersDesc')}
                  size="xs"
                  className="!font-normal leading-none !text-light-text-primary dark:!text-dark-text-primary mt-1"
                />
              </div>
              <FormField
                type="toggle"
                name="acceptSampleOrder"
                id="acceptSampleOrder"
                data-testid="accept-sample-order-toggle"
                onChange={(checked: unknown) => {
                  const isChecked = Boolean(checked);
                  setValue('acceptSampleOrder', isChecked, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  if (!isChecked) {
                    setValue('minSampleQty', undefined, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue('samplePrice', undefined, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
              />
            </div>

            {/* Min Sample Order Qty + Sample Price — only when toggle is on */}
            {acceptSampleOrder && (
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                data-testid="sample-order-fields-container"
              >
                <div
                  id="minSampleQty"
                  data-testid="min-sample-qty-container"
                >
                  <FormField
                    name="minSampleQty"
                    id="minSampleQty-input"
                    type="number"
                    className="!font-semibold"
                    label={t('minSampleOrderQtyLabel')}
                    placeholder={t('setNumber')}
                    data-testid="min-sample-order-field"
                  />
                </div>
                <div
                  id="samplePrice"
                  data-testid="sample-price-container"
                >
                  <FormField
                    name="samplePrice"
                    id="samplePrice-input"
                    type="number"
                    className="!font-semibold"
                    label={t('samplePriceLabel')}
                    placeholder={t('setNumber')}
                    data-testid="sample-price-field"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Certificates */}
        <div
          data-testid="certificates-container"
          className="pt-4 border-t border-light-input-border dark:border-dark-input-border"
        >
          <div
            data-testid="certificates-header-wrapper"
            className="flex items-center justify-between mb-4"
          >
            <div data-testid="certificates-header-title-wrapper">
              <TitleText
                text={t('certificates')}
                size="base"
                className="!font-semibold text-neutral-900 dark:text-dark-text-primary"
              />
              <InfoText
                text={t('certificatesDesc')}
                size="xs"
                className="!font-normal text-neutral-500 dark:text-dark-text-secondary mt-1"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="dark:border-pink-500 dark:text-pink-500 dark:hover:text-white !px-4 sm:!px-6 !py-2.5 sm:!py-3"
              onClick={() => {
                setEditingCertificateId(null);
                dispatch(openModal({ id: 'CERTIFICATE_MODAL' }));
              }}
              data-testid="verification-add-certificate-btn"
            >
              {t('addCertificateBtn')}
            </Button>
          </div>

          <div className="flex flex-col gap-4" data-testid="certificates-list">
            {certificatesDocuments.map((item: CertificateItem) => (
              <PreviewProfileItem
                key={item.uid}
                isDelete
                isEditable={true}
                onEdit={() => {
                  setEditingCertificateId(item.uid);
                  dispatch(openModal({ id: 'CERTIFICATE_MODAL' }));
                }}
                title={item.certificateName}
                attachments={[
                  {
                    url: item.url,
                    type: item.type || 'application/pdf',
                    id: item.uid,
                    name: item.name,
                  },
                ]}
                listAttachmentsMode={false}
                onDelete={() => handleDeleteCertificate(item.uid)}
                itemId={item.uid}
                dataTestId={`verification-certificate-item-${item.uid}`}
              />
            ))}
          </div>
        </div>
      </div>

      {modals?.['CERTIFICATE_MODAL']?.isOpen && (
        <CustomModal
          isOpen={modals['CERTIFICATE_MODAL'].isOpen}
          onClose={() => dispatch(closeModal({ id: 'CERTIFICATE_MODAL' }))}
          title={t('addCertificateModalTitle')}
          description={t('addCertificateModalDesc')}
        >
          <AddCertificateModal
            initData={editingCertificate}
            onSuccess={(cert, oldUid) =>
              handleAddCertificateSuccess(cert, oldUid)
            }
          />
        </CustomModal>
      )}

      {modals?.['VIEW_FILE']?.isOpen && (
        <PreviewFile
          open={modals['VIEW_FILE'].isOpen}
          onCancel={() => dispatch(closeModal({ id: 'VIEW_FILE' }))}
          data={modals['VIEW_FILE'].data || null}
        />
      )}
    </div>
  );
};

export default VerificationForm;
